/**
 * Shared metric value math used by trial scorecards (and any host-side grader).
 * Reduce lane/tool data → number, compare against a target.
 */
import { z } from 'zod';
import { applyWatchSelector, parseWatchSelector } from './watch-selector.js';
/** How selected data reduces to one number. */
export const MetricAggregateSchema = z.enum(['value', 'count', 'sum', 'avg', 'min', 'max']);
export const MetricComparatorSchema = z.enum(['>=', '<=', '>', '<', '==']);
/** absolute = compare raw value to target; delta = compare (value - baseline) to target. */
export const MetricBaselineModeSchema = z.enum(['absolute', 'delta']);
/**
 * Reduce lane data to the metric's number. Returns null when the data doesn't
 * yield a number (missing lane, selector miss, wrong shape).
 *
 * Aggregates:
 *   value        → Number(projected) when number / numeric string / boolean
 *   count        → array length (or object key count)
 *   sum/avg/min/max → over numeric elements of a projected array
 */
export function computeMetricValue(data, selector, aggregate) {
    let projected = data;
    if (selector) {
        const segments = parseWatchSelector(selector);
        if (!segments)
            return null;
        projected = applyWatchSelector(data, segments);
    }
    if (aggregate === 'value')
        return asNumber(projected);
    if (aggregate === 'count') {
        if (Array.isArray(projected))
            return projected.length;
        if (projected && typeof projected === 'object')
            return Object.keys(projected).length;
        return null;
    }
    if (!Array.isArray(projected))
        return null;
    const numbers = projected.map((el) => asNumber(el)).filter((n) => n !== null);
    if (numbers.length === 0)
        return null;
    switch (aggregate) {
        case 'sum':
            return numbers.reduce((a, b) => a + b, 0);
        case 'avg':
            return numbers.reduce((a, b) => a + b, 0) / numbers.length;
        case 'min':
            return Math.min(...numbers);
        case 'max':
            return Math.max(...numbers);
    }
}
function asNumber(value) {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value === 'boolean')
        return value ? 1 : 0;
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
/** The comparison half. `==` uses a small epsilon so float math can satisfy it. */
export function compareMetric(value, comparator, target) {
    switch (comparator) {
        case '>=':
            return value >= target;
        case '<=':
            return value <= target;
        case '>':
            return value > target;
        case '<':
            return value < target;
        case '==':
            return Math.abs(value - target) <= 1e-9;
    }
}
/**
 * Target used for compareMetric.
 * delta mode: baselineValue + target once baseline is captured; until then
 * comparison is deferred (caller should treat as not-yet-satisfiable).
 */
export function effectiveMetricTarget(board) {
    const mode = board.baselineMode ?? 'absolute';
    if (mode === 'delta') {
        if (board.baselineValue == null || !Number.isFinite(board.baselineValue))
            return null;
        return board.baselineValue + board.target;
    }
    return board.target;
}
//# sourceMappingURL=metric-value.js.map